import classNames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';

import {
  FormControl,
  FormHelperText,
  TextField,
  makeStyles,
} from '@material-ui/core';
import { Autocomplete as MuiAutocomplete } from '@material-ui/lab';

import LabelBlock from './LabelBlock';
import { useErrorStyles } from './form.styles';

export type AutocompleteItem = {
  value: string;
  label: string;
};

const useStyles = makeStyles((theme) => ({
  inputAutocomplete: {
    '& label': {
      transform: 'translate(0)',
      width: '100%',
      '& .label--required': {
        color: theme.palette.primary.main,
      },
      '& .label--icon-valid': {
        color: theme.palette.primary.main,
      },
    },
    '& .MuiAutocomplete-root': {
      backgroundColor: '#FFFFFF',
      border: '1px solid #999999',
      borderRadius: 4,
      marginTop: '2rem',
    },
    '& .MuiAutocomplete-inputRoot': {
      paddingLeft: '6px',
    },
    '& .MuiAutocomplete-icon': {
      top: 'calc(50% - 6px)',
    },
  },
}));

type Props = {
  label: string;
  menuItems: AutocompleteItem[];
  name?: string;
  value?: string;
  tooltip?: string;
  iconTooltip?: React.ReactNode;
  error?: string;
  touched?: boolean;
  required?: boolean;
  placeholder?: string;
  controlledState?: boolean;
  isErrorVisible?: boolean;
  bypassFilterOptions?: boolean;
  autoFocus?: boolean;
  noOptionsText?: string;
  onFocus?: (event: React.ChangeEvent<HTMLElement>) => void;
  onChange?: (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
    valueObj?: AutocompleteItem | null,
  ) => void;
  onBlur?: (
    event: React.ChangeEvent<HTMLElement | { name?: string; value: unknown }>,
  ) => void;
  onInputChange?: (value: string) => void;
  innerRef?: React.MutableRefObject<HTMLInputElement | null>;
};
export const Autocomplete: React.FC<Props> = ({
  name,
  label,
  menuItems,
  value,
  tooltip,
  iconTooltip,
  error,
  placeholder,
  controlledState,
  bypassFilterOptions,
  onInputChange,
  onChange,
  onBlur,
  onFocus,
  autoFocus,
  noOptionsText,
  touched,
  isErrorVisible,
  required = true,
  innerRef,
}) => {
  const classes = useStyles();
  const { errorFont, errorBlock } = useErrorStyles();

  const selectedValue = useMemo(
    () => menuItems.find((e) => e.value === value) || null,
    [menuItems, value],
  );

  const [inputValue, setInputValue] = useState(value || '');

  const changeInput = useCallback(
    (newInputValue = '') => {
      if (controlledState) {
        setInputValue(newInputValue);
      }
      onInputChange?.(newInputValue);
    },
    [controlledState, onInputChange],
  );

  return (
    <FormControl fullWidth className={classes.inputAutocomplete}>
      <LabelBlock
        label={label}
        required={required}
        tooltip={tooltip}
        iconTooltip={iconTooltip}
        error={error}
        touched={touched}
        isErrorVisible={isErrorVisible}
      />
      <MuiAutocomplete
        options={menuItems}
        value={selectedValue}
        noOptionsText={noOptionsText}
        {...(controlledState && { inputValue })}
        {...(bypassFilterOptions && { filterOptions: (o) => o })}
        getOptionLabel={(option) => (option as AutocompleteItem).label}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus={autoFocus}
            onFocus={onFocus}
            onChange={(event) => {
              changeInput(event.target.value);
            }}
            onBlur={(event) => {
              const eventWithValue: React.ChangeEvent<{
                name?: string;
                value: unknown;
              }> = {
                ...event,
                target: {
                  ...event.target,
                  name,
                  value: event?.target.value,
                },
                currentTarget: {
                  ...event.currentTarget,
                  name,
                  value: event?.target.value,
                },
              };
              onBlur?.(eventWithValue);

              // Set inputValue to value
              if (value !== inputValue && value) {
                changeInput(value);
              }
            }}
            placeholder={placeholder}
            ref={innerRef}
          />
        )}
        getOptionSelected={(option, value) =>
          (option as AutocompleteItem).value ===
          (value as AutocompleteItem).value
        }
        onChange={(event, valueObj) => {
          const eventWithValue: React.ChangeEvent<{
            name?: string;
            value: unknown;
          }> = {
            ...event,
            target: {
              ...event.target,
              name,
              value: (valueObj as AutocompleteItem)?.value,
            },
            currentTarget: {
              ...event.currentTarget,
              name,
              value: (valueObj as AutocompleteItem)?.value,
            },
          };

          changeInput((valueObj as AutocompleteItem)?.value);

          onChange?.(eventWithValue);
        }}
      />
      {error && (touched || isErrorVisible) && (
        <FormHelperText className={classNames(errorFont, errorBlock)}>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
};
